import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, X, CheckCircle, XCircle, Trash2, Edit3, Play,
  ExternalLink, Key, Building2, Calendar, Ticket, Zap, Mail,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  notification: { label: 'Notifications', icon: Mail,      color: 'text-teal-400' },
  crm:          { label: 'CRM',           icon: Building2, color: 'text-emerald-400' },
  calendar:     { label: 'Calendar',      icon: Calendar,  color: 'text-sky-400' },
  ticketing:    { label: 'Ticketing',     icon: Ticket,    color: 'text-violet-400' },
  automation:   { label: 'Automation',    icon: Zap,       color: 'text-amber-400' },
};

// ── Connection editor modal ──────────────────────────────────────────────────
const ConnectionModal = ({ integration, initial, onClose, onSave, onTest, saving }) => {
  const [form, setForm] = useState(initial || { name: integration.label, config: {}, enabled: true });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Email-only: live preview of the rendered HTML email
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);  // {subject, html, recipients, cc_recipients}

  const updateCfg = (k, v) => setForm(f => ({ ...f, config: { ...f.config, [k]: v } }));

  const handleTest = async () => {
    if (!initial?.id) { toast.error('Save the connection first, then test.'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await onTest(initial.id);
      setTestResult(r);
    } catch {
      setTestResult({ ok: false, message: 'Test request failed' });
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/voicefabric/integrations/email/preview`,
        {
          recipients: form.config.recipients || '',
          cc_recipients: form.config.cc_recipients || '',
          subject_template: form.config.subject_template || '',
        },
      );
      setPreviewData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0D1526] z-10">
          <div>
            <h2 className="text-white font-semibold flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-400" />
              {initial?.id ? 'Edit' : 'Connect'} {integration.label}
            </h2>
            <a
              href={integration.docs_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1 mt-0.5"
            >
              Where to find these <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs text-white/50 block mb-1">Connection name *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={`e.g. "${integration.label} — Production"`}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid="vf-conn-name"
            />
          </div>

          {integration.fields.map(f => (
            <div key={f.key}>
              <label className="text-xs text-white/50 block mb-1">
                {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              {f.type === 'textarea' ? (
                <textarea
                  rows={3}
                  value={form.config[f.key] || ''}
                  onChange={e => updateCfg(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                  data-testid={`vf-conn-${f.key}`}
                />
              ) : (
                <input
                  type={f.type === 'password' ? 'password' : 'text'}
                  value={form.config[f.key] || ''}
                  onChange={e => updateCfg(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                  data-testid={`vf-conn-${f.key}`}
                />
              )}
            </div>
          ))}

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              className={`w-10 h-5 rounded-full transition-colors relative ${form.enabled ? 'bg-teal-500' : 'bg-white/10'}`}
              onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-white/70">{form.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>

          {integration.id === 'email' && (
            <div className="border-t border-white/10 pt-4">
              <button
                onClick={handlePreview}
                disabled={previewing}
                className="flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 text-xs font-medium rounded-lg"
                data-testid="vf-email-preview-btn"
              >
                {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                Preview Email
              </button>
              <p className="text-[11px] text-white/30 mt-1.5">
                Renders the exact HTML email recipients will receive, using sample call data.
              </p>
            </div>
          )}

          {initial?.id && (
            <div className="border-t border-white/10 pt-4">
              <button
                onClick={handleTest}
                disabled={testing}
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-xs font-medium rounded-lg"
                data-testid="vf-conn-test-btn"
              >
                {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Test Connection
              </button>
              {testResult && (
                <div className={`mt-3 p-3 rounded-lg border text-xs flex items-start gap-2 ${testResult.ok
                    ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300'
                    : 'border-red-500/30 bg-red-500/5 text-red-300'}`}>
                  {testResult.ok
                    ? <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                  <p className="flex-1">{testResult.message}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#0D1526]">
          <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white text-sm">Cancel</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name}
            className="flex items-center gap-2 px-5 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-medium rounded-lg"
            data-testid="vf-save-conn-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {initial?.id ? 'Save Changes' : 'Save Connection'}
          </button>
        </div>
      </div>

      {previewData && (
        <EmailPreviewDrawer
          data={previewData}
          onClose={() => setPreviewData(null)}
        />
      )}
    </div>
  );
};

// ── Email preview drawer ─────────────────────────────────────────────────────
const EmailPreviewDrawer = ({ data, onClose }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    onClick={onClose}
    data-testid="vf-email-preview-drawer"
  >
    <div
      className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div>
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4 text-teal-400" />
            Email Preview
          </h2>
          <p className="text-xs text-white/40 mt-0.5">
            Sample call data — recipients will receive an email exactly like this.
          </p>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white" data-testid="vf-email-preview-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-6 py-4 border-b border-white/10 space-y-1 text-xs">
        <div className="flex gap-3">
          <span className="text-white/40 w-16 shrink-0">To:</span>
          <span className="text-white/90 break-all">
            {data.recipients?.length ? data.recipients.join(', ') : <em className="text-white/30">(no recipients yet)</em>}
          </span>
        </div>
        {data.cc_recipients?.length > 0 && (
          <div className="flex gap-3">
            <span className="text-white/40 w-16 shrink-0">Cc:</span>
            <span className="text-white/90 break-all">{data.cc_recipients.join(', ')}</span>
          </div>
        )}
        <div className="flex gap-3">
          <span className="text-white/40 w-16 shrink-0">Subject:</span>
          <span className="text-white font-medium" data-testid="vf-email-preview-subject">{data.subject}</span>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-slate-100">
        <iframe
          title="Email preview"
          srcDoc={data.html}
          className="w-full h-full border-0"
          sandbox=""
          data-testid="vf-email-preview-iframe"
        />
      </div>
    </div>
  </div>
);

// ── Integration card ─────────────────────────────────────────────────────────
const IntegrationCard = ({ integration, connections, onConnect, onEdit, onDelete }) => {
  const cat = CATEGORY_LABELS[integration.category] || CATEGORY_LABELS.automation;
  const myConns = connections.filter(c => c.integration_id === integration.id);
  const isStub = integration.status === 'stub';

  return (
    <div
      className={`bg-white/5 border rounded-xl p-4 transition-colors ${
        isStub ? 'border-white/5' : 'border-white/10 hover:border-white/20'
      }`}
      data-testid={`vf-int-${integration.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <cat.icon className={`w-4 h-4 ${cat.color}`} />
          <h3 className="text-white text-sm font-semibold">{integration.label}</h3>
        </div>
        {isStub && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/30">
            Soon
          </span>
        )}
      </div>
      <p className="text-white/30 text-xs mb-3">{cat.label}</p>

      {myConns.length === 0 ? (
        <button
          onClick={() => onConnect(integration)}
          className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            isStub
              ? 'bg-white/5 text-white/50 hover:bg-white/10'
              : 'bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 border border-teal-500/20'
          }`}
          data-testid={`vf-connect-${integration.id}`}
        >
          <Plus className="w-3 h-3" />
          Connect
        </button>
      ) : (
        <div className="space-y-1.5">
          {myConns.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 bg-white/5 rounded-lg px-2.5 py-1.5">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <div className={`w-1.5 h-1.5 rounded-full ${c.enabled ? 'bg-emerald-400' : 'bg-white/20'}`} />
                <span className="text-white/70 text-xs truncate">{c.name}</span>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  onClick={() => onEdit(integration, c)}
                  className="p-1 text-white/30 hover:text-teal-400 transition-colors"
                  data-testid={`vf-conn-edit-${c.id}`}
                >
                  <Edit3 className="w-3 h-3" />
                </button>
                <button
                  onClick={() => onDelete(c)}
                  className="p-1 text-white/30 hover:text-red-400 transition-colors"
                  data-testid={`vf-conn-delete-${c.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={() => onConnect(integration)}
            className="w-full text-xs text-teal-400/70 hover:text-teal-300 py-1"
          >
            + Add another
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main integrations tab ────────────────────────────────────────────────────
export const IntegrationsTab = ({ domainId, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [integrations, setIntegrations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalInt, setModalInt] = useState(null);
  const [modalInitial, setModalInitial] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadConnections = useCallback(async () => {
    if (!domainId) return;
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/voicefabric/connections`, { headers });
      setConnections(r.data.connections || []);
    } catch {
      toast.error('Failed to load connections');
    }
  }, [domainId, headers]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [intRes] = await Promise.all([
          axios.get(`${API}/admin/voicefabric/integrations`, { headers }),
          loadConnections(),
        ]);
        if (active) setIntegrations(intRes.data.integrations || []);
      } catch {
        if (active) toast.error('Failed to load integration catalog');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [headers, loadConnections]);

  const handleConnect = (integration) => {
    setModalInt(integration);
    setModalInitial(null);
  };
  const handleEdit = (integration, conn) => {
    setModalInt(integration);
    setModalInitial(conn);
  };
  const handleClose = () => { setModalInt(null); setModalInitial(null); };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (modalInitial?.id) {
        await axios.put(
          `${API}/admin/domains/${domainId}/voicefabric/connections/${modalInitial.id}`,
          { name: form.name, config: form.config, enabled: form.enabled },
          { headers }
        );
        toast.success('Connection updated');
      } else {
        await axios.post(
          `${API}/admin/domains/${domainId}/voicefabric/connections`,
          { integration_id: modalInt.id, name: form.name, config: form.config, enabled: form.enabled },
          { headers }
        );
        toast.success(`${modalInt.label} connected`);
      }
      handleClose();
      loadConnections();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x.msg).join('; ') : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (conn) => {
    if (!window.confirm(`Delete connection "${conn.name}"? Any rules using it will stop dispatching.`)) return;
    try {
      await axios.delete(`${API}/admin/domains/${domainId}/voicefabric/connections/${conn.id}`, { headers });
      toast.success('Connection deleted');
      loadConnections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleTest = async (connId) => {
    const r = await axios.post(
      `${API}/admin/domains/${domainId}/voicefabric/connections/${connId}/test`,
      {}, { headers }
    );
    return r.data;
  };

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-white/20 animate-spin" /></div>
  );

  const grouped = Object.keys(CATEGORY_LABELS).reduce((acc, cat) => {
    acc[cat] = integrations.filter(i => i.category === cat);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <p className="text-white/50 text-sm">
        Connect external systems so VoiceFabric rules can push data out automatically.
        Credentials are stored per-domain and masked after saving.
      </p>

      {Object.entries(grouped).map(([cat, items]) => {
        if (!items.length) return null;
        const meta = CATEGORY_LABELS[cat];
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <meta.icon className={`w-4 h-4 ${meta.color}`} />
              <h3 className="text-white/70 text-xs font-semibold uppercase tracking-widest">{meta.label}</h3>
              <span className="text-white/20 text-xs">({items.length})</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(i => (
                <IntegrationCard
                  key={i.id}
                  integration={i}
                  connections={connections}
                  onConnect={handleConnect}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        );
      })}

      {modalInt && (
        <ConnectionModal
          integration={modalInt}
          initial={modalInitial}
          onClose={handleClose}
          onSave={handleSave}
          onTest={handleTest}
          saving={saving}
        />
      )}
    </div>
  );
};
